import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

const CommunityServiceAdmin = ({ user, db, isAdmin }) => {
    const [editLockDate, setEditLockDate] = useState(null);
    const [communityServiceAdmins, setCommunityServiceAdmins] = useState([]);
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadSettings();
        loadCommunityServiceAdmins();
    }, []);

    const loadSettings = async () => {
        try {
            const configDoc = await getDoc(doc(db, 'community-service-config', 'settings'));
            if (configDoc.exists()) {
                const data = configDoc.data();
                setEditLockDate(new Date(data.editLockDate || Date.now() - (14 * 24 * 60 * 60 * 1000)));
            } else {
                // Default to 2 weeks ago
                const twoWeeksAgo = new Date();
                twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
                setEditLockDate(twoWeeksAgo);
            }
        } catch (error) {
            console.error('Error loading settings:', error);
        }
        setLoading(false);
    };

    const loadCommunityServiceAdmins = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'communityServiceAdmins'));
            const admins = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                // Only include admins that haven't been removed
                if (!data.removed) {
                    admins.push({ id: doc.id, ...data });
                }
            });
            setCommunityServiceAdmins(admins);
        } catch (error) {
            console.error('Error loading community service admins:', error);
        }
    };

    const saveEditLockDate = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'community-service-config', 'settings'), {
                editLockDate: editLockDate.toISOString(),
                lastUpdated: new Date().toISOString(),
                updatedBy: user.email
            }, { merge: true });
            alert('Edit lock date updated successfully!');
        } catch (error) {
            console.error('Error saving edit lock date:', error);
            alert('Error saving settings. Please try again.');
        }
        setSaving(false);
    };

    const addCommunityServiceAdmin = async () => {
        if (!newAdminEmail.trim()) {
            alert('Please enter a valid email address');
            return;
        }

        setSaving(true);
        try {
            // Add user to community service admins collection
            await setDoc(doc(db, 'communityServiceAdmins', newAdminEmail.trim()), {
                email: newAdminEmail.trim(),
                addedBy: user.email,
                addedAt: new Date().toISOString()
            });

            setNewAdminEmail('');
            loadCommunityServiceAdmins();
            alert('Community service admin added successfully!');
        } catch (error) {
            console.error('Error adding community service admin:', error);
            alert('Error adding admin. Please try again.');
        }
        setSaving(false);
    };

    const removeCommunityServiceAdmin = async (adminEmail) => {
        if (!window.confirm(`Remove ${adminEmail} as community service admin?`)) {
            return;
        }

        setSaving(true);
        try {
            await setDoc(doc(db, 'communityServiceAdmins', adminEmail), {
                email: adminEmail,
                removed: true,
                removedBy: user.email,
                removedAt: new Date().toISOString()
            });

            loadCommunityServiceAdmins();
            alert('Community service admin removed successfully!');
        } catch (error) {
            console.error('Error removing community service admin:', error);
            alert('Error removing admin. Please try again.');
        }
        setSaving(false);
    };

    const setEditLockToWeeksAgo = (weeks) => {
        const date = new Date();
        date.setDate(date.getDate() - (weeks * 7));
        setEditLockDate(date);
    };

    if (!isAdmin) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-red-800 font-semibold">Access Denied</div>
                    <div className="text-red-600">Only site administrators can access community service settings.</div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg text-gray-600">Loading settings...</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-[#782953]">Community Service Administration</h2>
                    <div className="text-sm text-gray-600">
                        Site Admin Panel
                    </div>
                </div>

                <div className="space-y-8">
                    {/* Edit Lock Date Settings */}
                    <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Lock Date</h3>
                        <div className="text-sm text-gray-600 mb-4">
                            Students cannot edit community service hours before this date (admins can always edit).
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Current Edit Lock Date
                                </label>
                                <input
                                    type="date"
                                    value={editLockDate ? `${editLockDate.getFullYear()}-${String(editLockDate.getMonth() + 1).padStart(2, '0')}-${String(editLockDate.getDate()).padStart(2, '0')}` : ''}
                                    onChange={(e) => setEditLockDate(new Date(e.target.value + 'T00:00:00'))}
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#782953]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Quick Set
                                </label>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setEditLockToWeeksAgo(1)}
                                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    >
                                        1 Week Ago
                                    </button>
                                    <button
                                        onClick={() => setEditLockToWeeksAgo(2)}
                                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    >
                                        2 Weeks Ago
                                    </button>
                                    <button
                                        onClick={() => setEditLockToWeeksAgo(4)}
                                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    >
                                        1 Month Ago
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <button
                                onClick={saveEditLockDate}
                                disabled={saving}
                                className="bg-[#782953] text-white px-4 py-2 rounded hover:bg-opacity-90 disabled:bg-gray-400"
                            >
                                {saving ? 'Saving...' : 'Save Edit Lock Date'}
                            </button>
                        </div>

                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                            <div className="text-sm text-yellow-800">
                                <strong>Current Setting:</strong> Students cannot edit hours before{' '}
                                {editLockDate ? editLockDate.toLocaleDateString() : 'Not Set'}
                            </div>
                        </div>
                    </div>

                    {/* Community Service Admins Management */}
                    <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Community Service Administrators</h3>
                        <div className="text-sm text-gray-600 mb-4">
                            Community service admins can view all reports but cannot change system settings.
                        </div>

                        {/* Add New Admin */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Add Community Service Administrator
                            </label>
                            <div className="flex space-x-2">
                                <input
                                    type="email"
                                    placeholder="admin@svots.edu"
                                    value={newAdminEmail}
                                    onChange={(e) => setNewAdminEmail(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#782953]"
                                />
                                <button
                                    onClick={addCommunityServiceAdmin}
                                    disabled={saving || !newAdminEmail.trim()}
                                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                                >
                                    {saving ? 'Adding...' : 'Add Admin'}
                                </button>
                            </div>
                        </div>

                        {/* Current Admins List */}
                        <div>
                            <h4 className="font-medium text-gray-800 mb-3">Current Community Service Administrators</h4>
                            {communityServiceAdmins.length === 0 ? (
                                <div className="text-gray-500 text-sm">No community service administrators assigned.</div>
                            ) : (
                                <div className="space-y-2">
                                    {communityServiceAdmins.map((admin) => (
                                        <div key={admin.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                                            <div>
                                                <div className="font-medium text-gray-800">{admin.name || 'Unknown Name'}</div>
                                                <div className="text-sm text-gray-600">{admin.id}</div>
                                            </div>
                                            <button
                                                onClick={() => removeCommunityServiceAdmin(admin.id)}
                                                disabled={saving}
                                                className="text-red-600 hover:text-red-800 text-sm font-medium disabled:text-gray-400"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Usage Statistics */}
                    <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">System Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <div className="text-gray-600">Database Collections:</div>
                                <ul className="mt-1 text-gray-800">
                                    <li>• community-service-logs (weekly entries)</li>
                                    <li>• community-service-config (settings)</li>
                                    <li>• users (admin permissions)</li>
                                </ul>
                            </div>
                            <div>
                                <div className="text-gray-600">Access Levels:</div>
                                <ul className="mt-1 text-gray-800">
                                    <li>• Students: Log own hours, view own reports</li>
                                    <li>• CS Admins: View all reports</li>
                                    <li>• Site Admins: Full control</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommunityServiceAdmin;
